"use client";
/*
 * Documentation:
 * Accordion — https://app.subframe.com/4f62853ee923/library?component=Accordion_d2e81e20-863a-4027-826a-991d8910efd9
 * Button — https://app.subframe.com/4f62853ee923/library?component=Button_3b777358-b86b-40af-9327-891efc6826fe
 * DC_Reading — https://app.subframe.com/4f62853ee923/library?component=DC_Reading_0c027fbe-5870-45d1-8c71-e2c79d50fb5f
 * DC_Text_Citation — https://app.subframe.com/4f62853ee923/library?component=DC_Text_Citation_d3e77a6d-0e14-44c6-8d63-65f4952d1644
 * Icon Button — https://app.subframe.com/4f62853ee923/library?component=Icon+Button_af9405b1-8c54-4e01-9786-5aad308224f6
 * Text Field — https://app.subframe.com/4f62853ee923/library?component=Text+Field_be48ca43-f8e7-4c0e-8870-d219ea11abfe
 */

import React from "react";
import { FeatherArrowUpRight } from "@subframe/core";
import { FeatherLink2 } from "@subframe/core";
import { FeatherSearch } from "@subframe/core";
import * as SubframeUtils from "../utils";
import { Accordion } from "./Accordion";
import { Button } from "./Button";
import { Dc_Text_Citation } from "./Dc_Text_Citation";
import { IconButton } from "./IconButton";
import { TextField } from "./TextField";

interface HeaderStackProps extends React.HTMLAttributes<HTMLDivElement> {
  isMinimized?: boolean;
  isSearched?: boolean;
  textSearchSlot?: React.ReactNode;
  cycleButtonSlot?: React.ReactNode;
  resultCount?: React.ReactNode;
  otherResultsStack?: React.ReactNode;
  expandButtonSlot?: React.ReactNode;
  actionSlot?: React.ReactNode;
  searchSlot?: React.ReactNode;
  className?: string;
}

const HeaderStack = React.forwardRef<HTMLDivElement, HeaderStackProps>(
  function HeaderStack(
    {
      isMinimized = false,
      isSearched = false,
      textSearchSlot,
      cycleButtonSlot,
      resultCount,
      otherResultsStack,
      expandButtonSlot,
      actionSlot,
      searchSlot,
      className,
      ...otherProps
    }: HeaderStackProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/cfa4d397 flex w-full flex-col items-start gap-4 px-4 py-4",
          { "flex-col flex-nowrap gap-0 px-4 py-4": isMinimized },
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <div
          className={SubframeUtils.twClassNames(
            "flex w-full items-start justify-between",
            { "flex-col flex-nowrap gap-4": isMinimized }
          )}
        >
          {expandButtonSlot ? (
            <div className="flex items-start justify-between">
              {expandButtonSlot}
            </div>
          ) : null}
          {actionSlot ? (
            <div
              className={SubframeUtils.twClassNames("flex items-start gap-2", {
                "flex-col flex-nowrap gap-4": isMinimized,
              })}
            >
              {actionSlot}
            </div>
          ) : null}
        </div>
        {searchSlot ? (
          <div className="flex w-full flex-col items-start gap-4">
            {searchSlot}
          </div>
        ) : null}
        {textSearchSlot ? (
          <div
            className={SubframeUtils.twClassNames(
              "hidden w-full flex-col items-start gap-6",
              { flex: isSearched }
            )}
          >
            {textSearchSlot}
          </div>
        ) : null}
      </div>
    );
  }
);

interface OtherResultsStackProps extends React.HTMLAttributes<HTMLDivElement> {
  resultsMainSlot?: React.ReactNode;
  resultsExcursionSlot?: React.ReactNode;
  className?: string;
}

const OtherResultsStack = React.forwardRef<
  HTMLDivElement,
  OtherResultsStackProps
>(function OtherResultsStack(
  {
    resultsMainSlot,
    resultsExcursionSlot,
    className,
    ...otherProps
  }: OtherResultsStackProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex w-full flex-col items-start gap-6",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-200" />
      {resultsMainSlot ? (
        <div className="flex w-full flex-col items-start gap-6">
          {resultsMainSlot}
        </div>
      ) : null}
      {resultsExcursionSlot ? (
        <div className="flex w-full flex-col items-start gap-6">
          {resultsExcursionSlot}
        </div>
      ) : null}
    </div>
  );
});

interface TextSearchExcursionItemProps
  extends React.HTMLAttributes<HTMLDivElement> {
  excursionName?: React.ReactNode;
  className?: string;
}

const TextSearchExcursionItem = React.forwardRef<
  HTMLDivElement,
  TextSearchExcursionItemProps
>(function TextSearchExcursionItem(
  { excursionName, className, ...otherProps }: TextSearchExcursionItemProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/a7ed7511 flex w-full cursor-pointer items-start gap-4",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {excursionName ? (
        <span className="grow shrink-0 basis-0 text-caption-bold font-caption-bold text-default-font group-hover/a7ed7511:no-underline">
          {excursionName}
        </span>
      ) : null}
      <IconButton
        variant="neutral-primary"
        size="small"
        icon={<FeatherArrowUpRight />}
      />
    </div>
  );
});

interface TextSearchExcursionItemStackProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  location?: "excursion";
  className?: string;
}

const TextSearchExcursionItemStack = React.forwardRef<
  HTMLDivElement,
  TextSearchExcursionItemStackProps
>(function TextSearchExcursionItemStack(
  {
    children,
    location = "excursion",
    className,
    ...otherProps
  }: TextSearchExcursionItemStackProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex w-full flex-col items-start gap-4",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <span className="w-full text-caption font-caption text-subtext-color">
        Results in Excursions
      </span>
      {children ? (
        <div className="flex w-full flex-col items-start gap-4">{children}</div>
      ) : null}
    </div>
  );
});

interface TextSearchBackMainItemProps
  extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const TextSearchBackMainItem = React.forwardRef<
  HTMLDivElement,
  TextSearchBackMainItemProps
>(function TextSearchBackMainItem(
  { className, ...otherProps }: TextSearchBackMainItemProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex w-full cursor-pointer items-center gap-4",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <span className="grow shrink-0 basis-0 text-caption font-caption text-subtext-color">
        Results in Main Text
      </span>
      <Button variant="neutral-primary" size="small">
        Show
      </Button>
    </div>
  );
});

interface SegmentItemProps extends React.HTMLAttributes<HTMLDivElement> {
  heading?: React.ReactNode;
  segmentContentStack?: React.ReactNode;
  isActive?: boolean;
  className?: string;
}

const SegmentItem = React.forwardRef<HTMLDivElement, SegmentItemProps>(
  function SegmentItem(
    {
      heading,
      segmentContentStack,
      isActive = false,
      className,
      ...otherProps
    }: SegmentItemProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/2e2cb01a flex w-full cursor-pointer flex-col items-start gap-4 border-t border-solid border-neutral-border hover:bg-transparent",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <Accordion
          trigger={
            <div
              className={SubframeUtils.twClassNames(
                "flex w-full items-center gap-2 px-4 py-3 group-hover/2e2cb01a:bg-neutral-100",
                { "bg-neutral-100": isActive }
              )}
            >
              {heading ? (
                <span className="grow shrink-0 basis-0 text-body-bold font-body-bold text-default-font">
                  {heading}
                </span>
              ) : null}
              <Accordion.Chevron />
            </div>
          }
        >
          {segmentContentStack ? (
            <div className="flex flex-col items-start gap-2 px-4 py-4 overflow-auto">
              {segmentContentStack}
            </div>
          ) : null}
        </Accordion>
      </div>
    );
  }
);

interface IndexItemProps extends React.HTMLAttributes<HTMLDivElement> {
  text?: React.ReactNode;
  layer?: "section" | "subsection" | "subsubsection";
  isExcursion?: boolean;
  variant?: "inactive" | "active" | "scrolled-past";
  className?: string;
}

const IndexItem = React.forwardRef<HTMLDivElement, IndexItemProps>(
  function IndexItem(
    {
      text,
      layer = "section",
      isExcursion = false,
      variant = "inactive",
      className,
      ...otherProps
    }: IndexItemProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/09745787 flex w-full cursor-pointer items-center gap-1",
          {
            "pl-4 pr-0 py-0": layer === "subsubsection",
            "pl-3 pr-0 py-0": layer === "subsection",
          },
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {text ? (
          <span
            className={SubframeUtils.twClassNames(
              "grow shrink-0 basis-0 text-caption font-caption text-default-font group-hover/09745787:underline",
              {
                "text-subtext-color": variant === "scrolled-past",
                "text-caption-bold font-caption-bold text-compcolorrcindexitemactive":
                  variant === "active",
              }
            )}
          >
            {text}
          </span>
        ) : null}
        <FeatherLink2
          className={SubframeUtils.twClassNames(
            "hidden text-caption font-caption text-default-font",
            {
              "text-subtext-color": variant === "scrolled-past",
              "text-caption-bold font-caption-bold text-compcolorrcindexitemactive":
                variant === "active",
              "inline-flex": isExcursion,
            }
          )}
        />
      </div>
    );
  }
);

interface IndexItemStackProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const IndexItemStack = React.forwardRef<HTMLDivElement, IndexItemStackProps>(
  function IndexItemStack(
    { children, className, ...otherProps }: IndexItemStackProps,
    ref
  ) {
    return children ? (
      <div
        className={SubframeUtils.twClassNames(
          "flex w-full flex-col items-start gap-4",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {children}
      </div>
    ) : null;
  }
);

interface ReferenceItemStackProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const ReferenceItemStack = React.forwardRef<
  HTMLDivElement,
  ReferenceItemStackProps
>(function ReferenceItemStack(
  { children, className, ...otherProps }: ReferenceItemStackProps,
  ref
) {
  return children ? (
    <div
      className={SubframeUtils.twClassNames(
        "flex w-full flex-col items-start gap-8",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {children}
    </div>
  ) : null;
});

interface ReferenceItemProps
  extends React.ComponentProps<typeof Dc_Text_Citation> {
  children?: React.ReactNode;
  className?: string;
}

const ReferenceItem = React.forwardRef<
  React.ElementRef<typeof Dc_Text_Citation>,
  ReferenceItemProps
>(function ReferenceItem(
  { children, className, ...otherProps }: ReferenceItemProps,
  ref
) {
  return (
    <Dc_Text_Citation
      className={SubframeUtils.twClassNames(
        "group/e59f55c4 cursor-pointer",
        className
      )}
      linkSlot={
        <>
          <Dc_Text_Citation.SourceButton>
            Google Scholar
          </Dc_Text_Citation.SourceButton>
          <Dc_Text_Citation.SourceButton>
            Digital Library
          </Dc_Text_Citation.SourceButton>
        </>
      }
      text="I. Boglaev, “A numerical method for solving nonlinear integro-differential equations of Fredholm type,” J. Comput. Math., vol. 34, no. 3, pp. 262–284, May 2016, doi: 10.4208/jcm.1512-m2015-0241."
      ref={ref}
      {...otherProps}
    >
      {children ? (
        <div className="flex-col items-start gap-2 inline inline! group-hover/e59f55c4:inline">
          {children}
        </div>
      ) : null}
    </Dc_Text_Citation>
  );
});

interface ReferenceItemIndexProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  children?: React.ReactNode;
  className?: string;
}

const ReferenceItemIndex = React.forwardRef<
  HTMLSpanElement,
  ReferenceItemIndexProps
>(function ReferenceItemIndex(
  { children, className, ...otherProps }: ReferenceItemIndexProps,
  ref
) {
  return children ? (
    <span
      className={SubframeUtils.twClassNames(
        "text-caption font-caption text-compcolorrcreferenceitemindex inline pr-2",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {children}
    </span>
  ) : null;
});

interface FigureItemStackProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const FigureItemStack = React.forwardRef<HTMLDivElement, FigureItemStackProps>(
  function FigureItemStack(
    { children, className, ...otherProps }: FigureItemStackProps,
    ref
  ) {
    return children ? (
      <div
        className={SubframeUtils.twClassNames(
          "flex w-full flex-col items-start gap-4",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {children}
      </div>
    ) : null;
  }
);

interface SearchBoxProps extends React.ComponentProps<typeof TextField> {
  className?: string;
}

const SearchBox = React.forwardRef<
  React.ElementRef<typeof TextField>,
  SearchBoxProps
>(function SearchBox({ className, ...otherProps }: SearchBoxProps, ref) {
  return (
    <TextField
      className={SubframeUtils.twClassNames("h-auto w-full", className)}
      label=""
      helpText=""
      icon={<FeatherSearch />}
      ref={ref}
      {...otherProps}
    >
      <TextField.Input placeholder="Search" />
    </TextField>
  );
});

interface Dc_ReadingRootProps extends React.HTMLAttributes<HTMLDivElement> {
  referenceSlot?: React.ReactNode;
  indexSlot?: React.ReactNode;
  contentStack?: React.ReactNode;
  figuresSlot?: React.ReactNode;
  headerSlot?: React.ReactNode;
  className?: string;
}

const Dc_ReadingRoot = React.forwardRef<HTMLDivElement, Dc_ReadingRootProps>(
  function Dc_ReadingRoot(
    {
      referenceSlot,
      indexSlot,
      contentStack,
      figuresSlot,
      headerSlot,
      className,
      ...otherProps
    }: Dc_ReadingRootProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex-col items-start rounded-lg bg-neutral-50 inline-flex",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {headerSlot ? (
          <div className="flex w-full flex-col items-start">{headerSlot}</div>
        ) : null}
        {contentStack ? (
          <div className="flex w-full flex-col items-start">{contentStack}</div>
        ) : null}
      </div>
    );
  }
);

export const Dc_Reading = Object.assign(Dc_ReadingRoot, {
  HeaderStack,
  OtherResultsStack,
  TextSearchExcursionItem,
  TextSearchExcursionItemStack,
  TextSearchBackMainItem,
  SegmentItem,
  IndexItem,
  IndexItemStack,
  ReferenceItemStack,
  ReferenceItem,
  ReferenceItemIndex,
  FigureItemStack,
  SearchBox,
});
